import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { KeyRound, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * First sign-in for a 13-17 account whose parent set the initial password. Once they choose their
 * own, the profile is theirs and the parent drops to view-only.
 */
export default function SetOwnPasswordPage() {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ password: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (f.password.length < 8) return toast.error('Use at least 8 characters');
    if (!/[a-zA-Z]/.test(f.password) || !/\d/.test(f.password)) {
      return toast.error('Include at least one letter and one number');
    }
    if (f.password !== f.confirm) return toast.error("Those passwords don't match");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/auth/set-own-password`, { new_password: f.password },
        getAuthHeader());
      if (r.data.access_token) localStorage.setItem('futurepath_token', r.data.access_token);
      await refreshUser?.();
      toast.success(r.data.message);
      nav(user?.onboarding_complete ? '/app/dashboard' : '/app/welcome');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not set that password');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-navy-gradient flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg rounded-3xl border border-white/10 bg-slate-900/70 backdrop-blur-xl p-7 md:p-9"
        data-testid="set-own-password-page">
        <KeyRound className="w-6 h-6 text-emerald-300" />
        <h1 className="font-outfit text-3xl font-semibold text-white mt-5">
          Pick your own password.
        </h1>
        <p className="text-sm text-slate-300 mt-3 leading-relaxed">
          Someone set this account up for you. Choose a password only you know, from then on this
          FuturePath is yours: your answers, your plan, your pace.
        </p>

        <div className="space-y-4 mt-6">
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">New password</Label>
            <Input type="password" value={f.password} onChange={e => setF({ ...f, password: e.target.value })}
              className="input-dark" data-testid="own-password" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Type it again</Label>
            <Input type="password" value={f.confirm} onChange={e => setF({ ...f, confirm: e.target.value })}
              className="input-dark" data-testid="own-password-confirm" />
          </div>
        </div>

        <Button onClick={submit} disabled={saving} data-testid="own-password-submit"
          className="w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold mt-6">
          {saving ? 'Saving…' : <>This is my password <ArrowRight className="w-4 h-4 ml-1.5" /></>}
        </Button>
        <p className="text-xs text-slate-300 mt-4">
          At least 8 characters, with a letter and a number.
        </p>
      </motion.div>
    </div>
  );
}
